// PRD.318 follow-up — treatment entry with optional Service linkage.
//
// One compact field that does both jobs the PRD asks for:
//
//     Treatment
//     [ Search services or enter treatment...        ]
//
// Picking a Service sets `serviceId` and SNAPSHOTS its current name as the
// label. Typing anything else leaves `serviceId` null and keeps the typed text.
// Nothing here creates a Service, and nothing fuzzy-matches free text against
// the catalogue — "Temporary dressing and reassess" stays exactly that.
//
// No billing side effects: selecting a Service records a catalogue reference
// and nothing more. No invoice, no line, no price, no charge.

import { useMemo, useRef, useState } from 'react';
import {
  resolveTreatmentEntry,
  searchTreatmentServices,
  treatmentFromService,
  type TreatmentEntryValue,
  type TreatmentServiceOption,
} from '@helios/data-access';
import { NavIcon } from '../../../shell/shared';

export function TreatmentEntryInput({
  services,
  placeholder,
  primary,
  autoFocus,
  initialText = '',
  initialServiceId = null,
  onCommit,
  onCancel,
}: {
  services: readonly TreatmentServiceOption[];
  placeholder: string;
  primary: string;
  autoFocus?: boolean;
  /**
   * Pre-filled label, used by Plan → Done so the clinician is not asked to
   * re-enter treatment they already documented.
   */
  initialText?: string;
  /** Service carried in with `initialText`. Preserved if that text is accepted. */
  initialServiceId?: string | null;
  /** Called with the resolved label + optional service reference. */
  onCommit: (value: TreatmentEntryValue) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initialText);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // What the field was seeded with. Captured once so later edits compare
  // against the original, not against whatever is currently typed.
  const seeded = useRef({ text: initialText, serviceId: initialServiceId });

  const matches = useMemo(
    () => (open ? searchTreatmentServices(services, text) : []),
    [open, services, text],
  );

  const commitTyped = () => {
    // Accepting a seeded label unchanged KEEPS its service; editing it drops
    // the reference. See `resolveTreatmentEntry`.
    const value = resolveTreatmentEntry({
      text,
      seededText: seeded.current.text,
      seededServiceId: seeded.current.serviceId,
    });
    if (!value.treatment) return;
    onCommit(value);
    setText('');
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="text"
          value={text}
          placeholder={placeholder}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            // Enter commits whatever is typed as FREE TEXT. Choosing a Service
            // is always an explicit click, so a fast typist never gets a
            // catalogue reference they did not ask for.
            if (e.key === 'Enter') {
              e.preventDefault();
              commitTyped();
            } else if (e.key === 'Escape') {
              setOpen(false);
              onCancel();
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            boxSizing: 'border-box',
            padding: '6px 8px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            outline: 'none',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-xs)',
            lineHeight: 'var(--line-height-body)',
            color: 'var(--text)',
            fontFamily: 'inherit',
          }}
        />
        <button
          type="button"
          onClick={commitTyped}
          disabled={!text.trim()}
          title="Add"
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: 'none',
            background: text.trim() ? primary : 'var(--text-disabled, #cbd5e1)',
            color: '#fff',
            cursor: text.trim() ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            flexShrink: 0,
          }}
        >
          <NavIcon name="check" />
        </button>
      </div>

      {/* Catalogue matches. Presented as a choice, never auto-applied — a chart
          condition or diagnosis must never pick a Service on the clinician's
          behalf. */}
      {open && matches.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 20,
            marginTop: 4,
            minWidth: 240,
            maxWidth: 360,
            maxHeight: 220,
            overflowY: 'auto',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          }}
        >
          {matches.map((svc) => (
            <button
              key={svc.id}
              type="button"
              onMouseDown={(e) => {
                // mousedown, not click: the input's blur would otherwise close
                // the list before the selection registers.
                e.preventDefault();
                onCommit(treatmentFromService(svc));
                setText('');
                setOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                border: 'none',
                background: 'transparent',
                color: 'var(--text)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {svc.name}
            </button>
          ))}
          <div
            style={{
              padding: '4px 10px 6px',
              fontSize: 'calc(var(--font-size-xs) * 0.85)',
              color: 'var(--text-placeholder, var(--text-muted))',
              borderTop: '1px solid var(--border)',
            }}
          >
            or press Enter to enter manually
          </div>
        </div>
      )}
    </div>
  );
}
